import { useCallback, useMemo, useRef } from 'react';
import { Filter, getEmptyFilterExpression, StringConditionType } from '@fabric-ui/components';
import { isDeepEqual } from '@fabric-ui/utils';
import {
  addGlobalOperand,
  addInlineOperand,
  countExpressions,
  deleteOperandAt,
  getUniqueFieldNames,
  replaceOperandAt,
  toggleAllLogicalOperators,
} from '../expressionOperations';
import { useFilterConfig, useFilterContext } from '../FilterContext';
import { LogicalOperatorType } from '../types';

type FilterOperand = NonNullable<Filter['and']>['operands'][number];

interface UseExpressionOperationsOptions {
  canDeleteLast?: boolean;
  defaultEmptyFilter?: Filter;
  defaultField?: string | null;
  fieldSelectionLimit?: { limit: number; message: string };
  hasListTab?: boolean;
  level?: number;
  rootOperator: LogicalOperatorType;
  rowsLimit?: { limit: number; message: string };
  supportParser?: boolean;
}

export const useExpressionOperations = (
  filter: Filter,
  setFilter: ((filter?: Filter) => void) | undefined,
  options: UseExpressionOperationsOptions
) => {
  const { disabled } = useFilterConfig();
  const { accountEntities } = useFilterContext();
  const fieldMap = accountEntities?.fieldMap ?? {};

  // Keep a ref to the latest filter so the mutator callbacks below don't need `filter` in their
  // dependency arrays. Editing one row changes the top-level `filter` reference on every keystroke,
  // which would otherwise force every row's callbacks (and therefore every row's memoized components)
  // to be recreated on every edit, regardless of which row actually changed.
  const filterRef = useRef(filter);
  filterRef.current = filter;

  const updateFilter = useCallback((nextFilter?: Filter) => setFilter?.(nextFilter), [setFilter]);

  const emptyExpressionOptions = useMemo(
    () => ({
      defaultEmptyFilter: options.defaultEmptyFilter,
      defaultField: options.defaultField,
      fieldMap,
      supportParser: options.supportParser,
    }),
    [fieldMap, options.defaultEmptyFilter, options.defaultField, options.supportParser]
  );

  const addGlobal = useCallback(
    (operator: LogicalOperatorType) => updateFilter(addGlobalOperand(filterRef.current, operator, emptyExpressionOptions)),
    [emptyExpressionOptions, updateFilter]
  );

  const toggleOperators = useCallback(
    () => updateFilter(toggleAllLogicalOperators(filterRef.current, options.hasListTab)),
    [options.hasListTab, updateFilter]
  );

  const replaceAt = useCallback(
    (index: number, value: FilterOperand) => updateFilter(replaceOperandAt(filterRef.current, options.rootOperator, index, value)),
    [options.rootOperator, updateFilter]
  );

  const deleteAt = useCallback(
    (index: number) =>
      updateFilter(
        deleteOperandAt(filterRef.current, options.rootOperator, index, {
          canDeleteLast: options.canDeleteLast,
          defaultEmptyFilter: options.defaultEmptyFilter,
          defaultField: options.defaultField,
          hasListTab: options.hasListTab,
          level: options.level,
          supportParser: options.supportParser,
        })
      ),
    [
      options.canDeleteLast,
      options.defaultEmptyFilter,
      options.defaultField,
      options.hasListTab,
      options.level,
      options.rootOperator,
      options.supportParser,
      updateFilter,
    ]
  );

  const addInline = useCallback(
    (index: number) => updateFilter(addInlineOperand(filterRef.current, options.rootOperator, index, { fieldMap, level: options.level })),
    [fieldMap, options.level, options.rootOperator, updateFilter]
  );

  const selectedFieldNames = useMemo(() => getUniqueFieldNames(filter), [filter]);
  const isOverFieldNamesLimit = !!options.fieldSelectionLimit && selectedFieldNames.size >= options.fieldSelectionLimit.limit;
  const isOverExpressionsLimit = !!options.rowsLimit && countExpressions(filter) >= options.rowsLimit.limit;
  const disableAddButton =
    isDeepEqual(getEmptyFilterExpression(null, options.supportParser ? StringConditionType.EQUALS : undefined), filter) ||
    isOverExpressionsLimit ||
    disabled;

  return {
    addGlobal,
    addInline,
    deleteAt,
    disableAddButton,
    isOverExpressionsLimit,
    isOverFieldNamesLimit,
    replaceAt,
    selectedFieldNames,
    toggleOperators,
  };
};
